import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { deliveryClient } from '../../../lib/connect/clients';
// Programs has a richer surface than the other delivery entities (separate
// Create / Get / Update / List / Delete instead of Upsert), so its hooks
// don't go through makeEntityHooks. Per-method explicit hooks here keep
// the request types accurate without flattening through an Upsert shim.
const KEY = 'program';
// Exported for consumers that must invalidate the program detail from
// outside this module (e.g. roster mutations flipping needsProgramLead).
export const PROGRAM_KEY = KEY;
export function usePrograms(req) {
    return useQuery({
        queryKey: [
            KEY,
            'list',
            req
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listPrograms({
                companyId: req.companyId,
                search: req.search ?? '',
                sectorId: req.sectorId ?? '',
                businessUnitId: req.businessUnitId ?? '',
                portfolioId: req.portfolioId ?? '',
                operationalStateId: req.operationalStateId ?? ''
            });
            return {
                items: resp.programs
            };
        },
        staleTime: 30000
    });
}
export function useProgram(id) {
    return useQuery({
        queryKey: [
            KEY,
            'detail',
            id
        ],
        queryFn: async ()=>{
            if (!id) throw new Error('Program id required');
            // canEdit / canManageQuality / canDeleteQualityScore are resolved
            // server-side (ryugu consults the policy spine); the UI renders them
            // and never re-implements policy.
            const resp = await deliveryClient.getProgram({
                id
            });
            return {
                program: resp.program,
                canEdit: resp.canEdit,
                canManageQuality: resp.canManageQuality,
                canManageContacts: resp.canManageContacts,
                canManageSelfAssessment: resp.canManageSelfAssessment,
                canViewSelfAssessment: resp.canViewSelfAssessment,
                canManageMembers: resp.canManageMembers,
                canDeleteQualityScore: resp.canDeleteQualityScore
            };
        },
        enabled: !!id,
        staleTime: 30000
    });
}
// Update takes the full Program proto; the page passes back the current
// Program with any field edits applied so we don't have to enumerate every
// mutable field here.
export function useUpdateProgram(opts) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await deliveryClient.updateProgram({
                id: args.id,
                program: args.program
            });
            return resp.program;
        },
        onSuccess: (data, args)=>{
            // Seed the detail cache with the mutation's returned (enriched)
            // program so inline editors never decide on a stale snapshot during
            // the invalidate-to-refetch window — the Role=Prime autofill reads
            // primeContractorCompanyId from this cache.
            if (data) {
                qc.setQueryData([
                    KEY,
                    'detail',
                    args.id
                ], (prev)=>prev ? {
                        ...prev,
                        program: data
                    } : prev);
            }
            qc.invalidateQueries({
                queryKey: [
                    KEY,
                    'list'
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    KEY,
                    'detail',
                    args.id
                ]
            });
            if (!opts?.silent) {
                toast.success('Program updated');
            }
        },
        onError: (err)=>{
            toast.error(`Failed to update program: ${err.message}`);
        }
    });
}
export function useDeleteProgram() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id)=>{
            await deliveryClient.deleteProgram({
                id
            });
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    KEY
                ]
            });
            toast.success('Program archived');
        },
        onError: (err)=>{
            toast.error(`Failed to archive program: ${err.message}`);
        }
    });
}
// Dry-run purge: exactly what a real purge would destroy, nothing touched.
// Admin-only server-side; enable only for admins with the dialog open.
export function usePurgeProgramPreview(id, enabled) {
    return useQuery({
        queryKey: [
            KEY,
            'purge-preview',
            id
        ],
        queryFn: ()=>deliveryClient.purgeProgram({
                id,
                dryRun: true
            }),
        enabled: enabled && !!id,
        staleTime: 0,
        gcTime: 0
    });
}
export function usePurgeProgram() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (req)=>deliveryClient.purgeProgram({
                id: req.id,
                confirmName: req.confirmName
            }),
        onSuccess: (resp)=>{
            qc.invalidateQueries({
                queryKey: [
                    KEY
                ]
            });
            const rows = resp.tables.reduce((n, t)=>n + Number(t.rowsDeleted), 0);
            toast.success(`Purged "${resp.programName}" — ${rows} records permanently destroyed`);
        },
        onError: (err)=>{
            toast.error(`Failed to purge program: ${err.message}`);
        }
    });
}
